import { clsx } from "clsx";
import { FC } from "react";
import { Body } from "../Typography";
import "./PositionCounter.css";

/**
 * Props for the PositionCounter component
 */
export interface PositionCounterProps {
  /**
   * The 1-based position of the current item within the list (e.g. 7).
   */
  current: number;

  /**
   * The total number of items currently known in the list (e.g. 25).
   */
  total: number;

  /**
   * Whether more items are still being lazy-loaded (e.g. additional infinite
   * scroll pages remain). When true, a trailing "+" is appended to the total
   * to signal the count is not yet final (e.g. "#7/25+").
   * @default false
   */
  hasMore?: boolean;

  /**
   * Additional CSS class name for the root element.
   */
  className?: string;
}

/**
 * PositionCounter component for the Indigo design system.
 *
 * A compact pill that communicates how far through a list the user currently is,
 * rendered as `#<current>/<total>` (e.g. "#7/25"). Used in detail drawers that
 * page through a filtered list of records so the user can track their progress.
 *
 * When the underlying list is still lazy-loading more pages, pass `hasMore` to
 * append a trailing "+" — the total reflects only what is loaded so far.
 *
 * @example
 * ```tsx
 * // Fully loaded list
 * <PositionCounter current={7} total={25} />        // -> #7/25
 *
 * // More pages still loading
 * <PositionCounter current={7} total={25} hasMore /> // -> #7/25+
 * ```
 */
export const PositionCounter: FC<PositionCounterProps> = ({
  current,
  total,
  hasMore = false,
  className,
}) => {
  return (
    <Body
      as="span"
      size="sm"
      className={clsx("indigo-position-counter", className)}
      aria-label={`Viewing ${current} of ${total}${hasMore ? " or more" : ""}`}
    >
      #{current}/{total}
      {hasMore ? "+" : ""}
    </Body>
  );
};

PositionCounter.displayName = "PositionCounter";
